import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, Target, Shield, Users, Sparkles, Phone, Stethoscope, ShoppingBag, MessageCircle, 
  Activity, FileText, Lock, Video, Bot, BookOpen, Calendar, Bell, Heart, MessagesSquare, 
  Palette, Check, ArrowRight
} from 'lucide-react';

export const AboutUs = () => {
  const features = {
    free: [
      {
        icon: <Brain className="w-6 h-6" />,
        title: "Mood Journal",
        description: "Log your daily mood with our intuitive journal. Track patterns over time with visualizations and gain insights into your emotional wellness. Select past dates to log missed entries."
      },
      {
        icon: <Stethoscope className="w-6 h-6" />,
        title: "Find Professional Help",
        description: "Search and connect with qualified psychiatrists and psychologists in your area. View profiles, specializations, and book appointments seamlessly."
      },
      {
        icon: <ShoppingBag className="w-6 h-6" />,
        title: "Wellness Store",
        description: "Browse curated mental health products including self-help books, meditation tools, journals, and wellness resources. Secure checkout with order history and PDF invoices."
      },
      {
        icon: <Phone className="w-6 h-6" />,
        title: "Emergency Support",
        description: "Quick one-tap access to crisis resources, emergency helpline numbers, and intervention services. Available 24/7 when you need support most."
      },
      {
        icon: <BookOpen className="w-6 h-6" />,
        title: "Content Library",
        description: "Access a rich collection of articles, guided exercises, coping strategies, educational resources, and mental wellness guides curated by professionals."
      },
      {
        icon: <Calendar className="w-6 h-6" />,
        title: "Calendar Integration",
        description: "Sync with Google Calendar and Microsoft Outlook to never miss appointments, therapy sessions, or medication reminders."
      },
      {
        icon: <Bell className="w-6 h-6" />,
        title: "Smart Notifications",
        description: "Receive timely push notifications for medications, appointments, wellness check-ins, and group therapy sessions."
      },
      {
        icon: <Heart className="w-6 h-6" />,
        title: "Medication Management",
        description: "Track prescribed medications with dosage, frequency, and reminders. Monitor adherence and maintain organized treatment records."
      },
      {
        icon: <Palette className="w-6 h-6" />,
        title: "Personalized Themes",
        description: "Choose between our beautiful Sunrise light mode and calming Dark mode. Your preference is saved and synced across sessions."
      }
    ],
    premium: [
      {
        icon: <Activity className="w-6 h-6" />,
        title: "MindPulse™",
        description: "Proactive wellness detection with gentle alerts when your mind needs care. Includes 15-90 second micro-recovery exercises: breathing techniques, grounding exercises, and cognitive resets."
      },
      {
        icon: <Bot className="w-6 h-6" />,
        title: "AI Companion 'Amigo'",
        description: "Your 24/7 AI-powered mental health companion powered by advanced language models. Have meaningful conversations, get coping strategies, and receive personalized support anytime."
      },
      {
        icon: <Video className="w-6 h-6" />,
        title: "Video Consultations",
        description: "Secure, WebRTC-based video calls with your connected healthcare providers. Features include audio/video controls, fullscreen mode, and end-to-end encryption."
      },
      {
        icon: <Lock className="w-6 h-6" />,
        title: "Private Mental Vault",
        description: "PIN-protected, encrypted space for your most private thoughts. Generate one-time access codes to securely share entries with your doctor during consultations."
      },
      {
        icon: <FileText className="w-6 h-6" />,
        title: "Doctor-Ready Summary",
        description: "Generate comprehensive PDF reports with mood graphs, symptom trends, medication adherence, and wellness insights. Share with healthcare providers for more productive appointments."
      },
      {
        icon: <MessageCircle className="w-6 h-6" />,
        title: "Just Talk",
        description: "Connect with peer supporters for meaningful, moderated conversations. A safe space to share your thoughts with others who understand, with mutual consent required."
      },
      {
        icon: <MessagesSquare className="w-6 h-6" />,
        title: "Mental Health Forums",
        description: "Join community discussions, share experiences, and connect with others on similar journeys. Post, comment, like, and share in a supportive environment."
      },
      {
        icon: <Users className="w-6 h-6" />,
        title: "Group Therapy Sessions",
        description: "Participate in virtual group therapy sessions. Connect with others, share experiences, and heal together in professionally guided sessions."
      },
      {
        icon: <Sparkles className="w-6 h-6" />,
        title: "Quantum Manifestation",
        description: "Guided visualization and manifestation techniques. Set daily intentions, practice gratitude journaling, and cultivate positive mental states through structured exercises."
      }
    ]
  };

  return (
    <div className="min-h-screen py-12" data-testid="about-us-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Brain className="w-16 h-16 mx-auto mb-6" style={{ color: 'var(--sunrise-orange)' }} />
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
            About HearMeOut
          </h1>
          <p className="text-xl max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--text-medium)' }}>
            A comprehensive mental health support platform making wellness more accessible, human, and judgment-free.
          </p>
        </div>

        {/* Introduction */}
        <div className="feature-card mb-12">
          <p className="leading-relaxed text-lg" style={{ color: 'var(--text-dark)' }}>
            Mental well-being is not a luxury—it's a daily need. Yet for many people, tracking emotions, 
            managing mental health challenges, staying consistent with medications, or simply finding someone 
            to talk to can feel overwhelming or inaccessible.
          </p>
          <p className="leading-relaxed text-lg mt-4" style={{ color: 'var(--text-dark)' }}>
            <strong>HearMeOut</strong> was created to bridge that gap. We've built a secure, thoughtfully designed 
            mental health support ecosystem with <strong>18+ features</strong> that empowers individuals to better 
            understand themselves, stay organized with their care, connect with professionals, and feel less alone 
            during difficult moments.
          </p>
        </div>

        {/* Mission Section */}
        <div className="feature-card mb-12" style={{ borderLeft: '4px solid var(--sunrise-dark-orange)' }}>
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-8 h-8" style={{ color: 'var(--sunrise-orange)' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
              Our Mission
            </h2>
          </div>
          <p className="leading-relaxed mb-6" style={{ color: 'var(--text-dark)' }}>
            Our mission is to support mental well-being through clarity, connection, and consistency. We aim to:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--sunrise-orange)' }} />
              <span style={{ color: 'var(--text-dark)' }}>Help individuals become more aware of their emotional patterns through daily tracking and AI insights</span>
            </li>
            <li className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--sunrise-orange)' }} />
              <span style={{ color: 'var(--text-dark)' }}>Reduce anxiety and stress through proactive tools like MindPulse™ and guided exercises</span>
            </li>
            <li className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--sunrise-orange)' }} />
              <span style={{ color: 'var(--text-dark)' }}>Enable safe human connection through peer support, forums, and group therapy</span>
            </li>
            <li className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--sunrise-orange)' }} />
              <span style={{ color: 'var(--text-dark)' }}>Bridge the gap between patients and healthcare providers with video consultations and secure data sharing</span>
            </li>
          </ul>
        </div>

        {/* Free Features */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
              Free Features
            </h2>
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22C55E' }}>
              Available to All Users
            </span>
          </div>
          <p className="mb-8" style={{ color: 'var(--text-medium)' }}>
            Start your mental wellness journey with these essential tools—completely free:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {features.free.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(255, 165, 0, 0.15)' }}>
                  <div style={{ color: 'var(--sunrise-orange)' }}>{feature.icon}</div>
                </div>
                <h3 className="font-bold mb-2" style={{ color: 'var(--text-black)' }}>{feature.title}</h3>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Premium Features */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
              Pro Features
            </h2>
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'rgba(168, 85, 247, 0.15)', color: '#A855F7' }}>
              Premium Subscription
            </span>
          </div>
          <p className="mb-8" style={{ color: 'var(--text-medium)' }}>
            Unlock the full power of HearMeOut with these advanced features:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {features.premium.map((feature, index) => (
              <div key={index} className="feature-card" style={{ background: 'linear-gradient(135deg, var(--card-bg), rgba(168, 85, 247, 0.05))' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(168, 85, 247, 0.15)' }}>
                  <div style={{ color: '#A855F7' }}>{feature.icon}</div>
                </div>
                <h3 className="font-bold mb-2" style={{ color: 'var(--text-black)' }}>{feature.title}</h3>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Doctor's Portal */}
        <div className="feature-card mb-12" style={{ background: 'linear-gradient(135deg, var(--card-bg), rgba(6, 182, 212, 0.05))' }}>
          <div className="flex items-center gap-3 mb-6">
            <Stethoscope className="w-8 h-8" style={{ color: '#06B6D4' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
              Doctor's Portal
            </h2>
          </div>
          <p className="mb-6" style={{ color: 'var(--text-dark)' }}>
            Healthcare providers get a dedicated dashboard to monitor and support their patients:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
              <div>
                <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Patient Connection System</span>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Generate secure 6-digit codes for patients to connect</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
              <div>
                <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Real-time Monitoring</span>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>View patient mood trends and wellness data</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
              <div>
                <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Secure Vault Access</span>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>View patient's private entries with their consent</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
              <div>
                <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Video Consultations</span>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Conduct secure video sessions with patients</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
              <div>
                <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Admin Verification</span>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Medical license verification for trust</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#06B6D4' }} />
              <div>
                <span className="font-semibold" style={{ color: 'var(--text-black)' }}>Patient Summaries</span>
                <p className="text-sm" style={{ color: 'var(--text-medium)' }}>Access comprehensive wellness reports</p>
              </div>
            </div>
          </div>
        </div>

        {/* Safety & Privacy */}
        <div className="feature-card mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8" style={{ color: '#22C55E' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
              Safety & Privacy
            </h2>
          </div>
          <p className="mb-6" style={{ color: 'var(--text-dark)' }}>
            Mental health data is deeply personal, and we treat it that way:
          </p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#22C55E' }}></div>
              <span style={{ color: 'var(--text-dark)' }}>End-to-end encryption for all sensitive data including vault entries</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#22C55E' }}></div>
              <span style={{ color: 'var(--text-dark)' }}>PIN-protected Private Vault with one-time doctor access codes</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#22C55E' }}></div>
              <span style={{ color: 'var(--text-dark)' }}>Moderated peer conversations for safety and respect</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#22C55E' }}></div>
              <span style={{ color: 'var(--text-dark)' }}>HIPAA-friendly video consultations with WebRTC</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#22C55E' }}></div>
              <span style={{ color: 'var(--text-dark)' }}>1-hour session timeout for account security</span>
            </li>
          </ul>
          <p className="font-semibold" style={{ color: 'var(--sunrise-dark-orange)' }}>
            User trust is not optional—it is foundational.
          </p>
        </div>

        {/* What We Are / Are Not */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="feature-card" style={{ borderTop: '4px solid #22C55E' }}>
            <h3 className="font-bold mb-4" style={{ color: '#22C55E' }}>What We Are</h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--text-dark)' }}>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#22C55E' }} />
                A comprehensive digital companion for mental well-being
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#22C55E' }} />
                A bridge between patients and healthcare providers
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#22C55E' }} />
                A safe space to track, reflect, and connect
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#22C55E' }} />
                A platform designed with empathy, safety, and privacy
              </li>
            </ul>
          </div>
          
          <div className="feature-card" style={{ borderTop: '4px solid #EF4444' }}>
            <h3 className="font-bold mb-4" style={{ color: '#EF4444' }}>What We Are Not</h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--text-dark)' }}>
              <li className="flex items-start gap-2">
                <span style={{ color: '#EF4444' }}>✗</span>
                A replacement for therapists, psychiatrists, or medical professionals
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: '#EF4444' }}>✗</span>
                A diagnostic or treatment tool
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: '#EF4444' }}>✗</span>
                A platform that encourages self-medication
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: '#EF4444' }}>✗</span>
                A substitute for emergency services
              </li>
            </ul>
          </div>
        </div>

        {/* Vision */}
        <div className="feature-card mb-12" style={{ background: 'linear-gradient(135deg, var(--card-bg), rgba(255, 165, 0, 0.1))' }}>
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
            Our Vision
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-dark)' }}>
            We envision a future where mental health support is:
          </p>
          <ul className="space-y-2 mb-6">
            <li style={{ color: 'var(--text-dark)' }}>• Less intimidating and more approachable</li>
            <li style={{ color: 'var(--text-dark)' }}>• More human with real connections and AI support</li>
            <li style={{ color: 'var(--text-dark)' }}>• Available when people need it—not only when they reach a breaking point</li>
            <li style={{ color: 'var(--text-dark)' }}>• Integrated with professional care for holistic wellness</li>
          </ul>
          <p style={{ color: 'var(--text-dark)' }}>
            By combining thoughtful technology, responsible AI assistance, and real human connection, 
            we aim to create a platform that supports people through both everyday stress and more challenging moments.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center feature-card">
          <Brain className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--sunrise-orange)' }} />
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
            You're Not Alone
          </h2>
          <p className="max-w-2xl mx-auto mb-8" style={{ color: 'var(--text-medium)' }}>
            Mental health journeys are deeply personal—but they don't have to be lonely. 
            Whether you're here to understand your emotions better, stay organized with your care, 
            or simply talk to someone who understands, we're here to support you—one step at a time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=register"
              className="px-8 py-4 rounded-xl btn-primary-cta hover:scale-105 transition-transform font-semibold flex items-center justify-center gap-2"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 rounded-xl font-semibold btn-secondary"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
